<template>
  <DatePickerSkeletonLoader v-if="isLoading" :num-of-loaders="numOfPresets" />
  <section v-else class="date-picker-wrapper" aria-label="Date picker">
    <VueDatePicker
      v-model="date"
      :range="!isWeekPicker"
      :multi-calendars="{ static: false }"
      week-start="0"
      :min-date="minDate"
      :max-date="maxDate"
      :format="formatRange"
      :enable-time-picker="false"
      ignore-time-validation
      :clearable="false"
      menu-class-name="date-picker-menu"
      position="right"
      :offset="0"
      :week-picker="isWeekPicker"
      :teleport="true"
      @internal-model-change="setTempDateRange"
      @open="open = true"
      @closed="open = false"
    >
      <template #dp-input="{ isMenuOpen }">
        <DatePickerTrigger
          :is-menu-open="isMenuOpen"
          :is-custom-range-applied="isCustomRangeApplied"
        />
      </template>
      <template
        #action-row="{
          selectDate, closePicker, disabled, internalModelValue,
        }"
      >
        <DatePickerActions
          :select-date="selectDate"
          :close-picker="closePicker"
          :disabled="disabled"
          :internal-model-value="internalModelValue"
          :is-week="isWeekPicker"
        />
      </template>
    </VueDatePicker>
    <DatePickerPresets
      :model-value="date"
      :selected-range="selectedRange"
      :formatted-presets="formattedPresets"
      :default-range="defaultRange"
      @update:model-value="updateDate"
    />
  </section>
</template>

<script setup>
import { computed, defineAsyncComponent, ref } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useRouter, useRoute } from 'vue-router';
import { useFilters } from '@/stores/filters/filters';
import { formatRange, isValidDate, areDateRangesEqual } from '@/helpers/date';
import DatePickerTrigger from '@/components/filter/dateFilters/DatePicker/DatePickerTrigger.vue';
import DatePickerSkeletonLoader from '@/components/shared/SkeletonLoaders/DatePickerSkeletonLoader.vue';
import { useTracking } from '@/stores/tracking/tracking';

const DatePickerPresets = defineAsyncComponent(() => import('@/components/filter/dateFilters/DatePicker/DatePickerPresets.vue'));
const DatePickerActions = defineAsyncComponent(() => import('@/components/filter/dateFilters/DatePicker/DatePickerActions.vue'));

defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
});

// Stores
const datePickerStore = useDatePicker();
const { setTempDateRange } = datePickerStore;
const {
  sortedRange, startDate, endDate, tempDateRange, datePickerVariant, presets,
} = storeToRefs(datePickerStore);

const { sendFilterRequest } = useFilters();

const router = useRouter();
const route = useRoute();

// State
const open = ref(false);

// Computed
const numOfPresets = computed(() => (Object.keys(presets?.value).length || 1) + 1);

const date = computed({
  get: () => sortedRange.value,
  set: () => {
    const sortedDates = (tempDateRange.value || []).sort((a, b) => a - b);

    if (sortedDates.length === 2) {
      const from = format(tempDateRange.value[0], 'yyyy-MM-dd');
      const to = format(tempDateRange.value[1], 'yyyy-MM-dd');

      useTracking().filterDateRange({ page: route.name, dates: { from, to } });
    }

    sendFilterRequest({ router });
  },
});

const maxDate = computed(() => {
  const maxDateObj = endDate.value ? new Date(`${endDate.value}T00:00:00`) : new Date();
  return isValidDate(maxDateObj) ? maxDateObj : null;
});

const minDate = computed(() => {
  const startDateObj = new Date(`${startDate.value}T00:00:00`);
  return isValidDate(startDateObj) ? startDateObj : null;
});

const isWeekPicker = computed(() => datePickerVariant.value === 'week');

const formattedPresets = computed(() => {
  const presetKeys = Object.keys(presets.value);
  const presetCopy = { ...presets.value };

  const presetsByCategory = presetKeys.reduce((acc, category) => {
    acc[category] = presetCopy[category].map((preset) => {
      const startDateObj = new Date(`${startDate.value}T00:00:00`);
      const maxDateObj = new Date(`${endDate.value}T00:00:00`);

      const presetStartDateObj = preset.range[0];
      const presetEndDateObj = preset.range[1];
      const isPresetStartAfterStartDate = isValidDate(startDateObj)
        ? presetStartDateObj >= startDateObj : true;
      const isPresetStartBeforeEndDate = isValidDate(maxDateObj)
        ? presetStartDateObj <= maxDateObj : true;
      const isPresetEndBeforeEndDate = isValidDate(maxDateObj)
        ? presetEndDateObj <= maxDateObj : true;
      const isPresetEndAfterStartDate = isValidDate(startDateObj)
        ? presetEndDateObj >= startDateObj : true;

      const isInRange = (isPresetStartAfterStartDate && isPresetStartBeforeEndDate)
          || (isPresetStartAfterStartDate && isPresetEndBeforeEndDate)
          || (isPresetEndBeforeEndDate && isPresetEndAfterStartDate);

      return {
        label: preset.label,
        value: preset.range,
        disabled: !isInRange,
      };
    });

    return acc;
  }, {});

  return presetsByCategory;
});

const selectedRange = computed(() => {
  const rangeCategory = Object.keys(presets.value);
  const currentRange = date.value;

  const selected = { category: '', range: {} };

  rangeCategory.forEach((category) => {
    // Below formats the preset ranges to be identical to those
    // we send to the backend, so we can compare
    presets.value[category].forEach((preset) => {
      const range = {
        from: format(preset.range[0], 'yyyy-MM-dd'),
        to: format(preset.range[1], 'yyyy-MM-dd'),
      };

      const res = [new Date(`${range.from}T00:00:00`), new Date(`${range.to}T00:00:00`)];

      if (areDateRangesEqual(res, currentRange)) {
        selected.category = category;
        selected.range = { label: preset.label, value: preset.range };
      }
    });
  });

  return selected;
});

// Get the This and Last dropdowns in sync with the same range unit
// Example: This Month and Last Month, or This Year and Last Year
const defaultRange = computed(() => {
  if (!selectedRange.value?.range?.value?.length) return {};

  if (['lastRange', 'thisRange'].includes(selectedRange.value?.category)) {
    const range = selectedRange.value?.range?.label?.split(' ');
    const [rangeType, rangeUnit] = range;
    const rangeTypeToSync = rangeType.toLowerCase() === 'this' ? 'last' : 'this';

    const rangeCategoryToSync = `${rangeTypeToSync}Range`;
    const rangeToSync = `${rangeTypeToSync} ${rangeUnit}`.toLowerCase();

    if (rangeType && rangeUnit) {
      const matchingPreset = (presets.value[rangeCategoryToSync]
      || []).find((preset) => preset.label.toLowerCase() === rangeToSync);

      return {
        category: rangeCategoryToSync,
        range: { label: matchingPreset?.label || '', value: matchingPreset?.range || [] },
      };
    }
  }

  return {};
});

// If the selectedRange computed found no match, we assume the applied range is Custom
const isCustomRangeApplied = computed(() => !selectedRange.value?.range?.value?.length);

// Methods
const updateDate = (value) => {
  // Set the date range from the clicked preset
  setTempDateRange(value);
  // Trigger the computed to 'set' and request the update
  date.value = value;
};
</script>

<style lang="less">
.date-picker-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid @stroke-default;
  background-color: @bg-default;
  border-radius: 8px;
}

.dp__main {
  width: auto;
}

.dp__outer_menu_wrap {
  @import '@/less/mixins';

  .date-picker-menu {
    .shadow();
    border: none;
  }

  .dp__month_year_select {
    font-size: 14px;
    font-weight: 400;
  }

  .dp__calendar_header_item {
    color: @text-lighter;
  }

  .dp__action_select {
    height: 32px;
    width: 63px;
    border-radius: 28px;
    font-size: 12px;
    font-weight: 500;
    color: @white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dp__arrow_top {
    display: none;
  }
}

.dp__theme_light {
  --dp-hover-color: @bg-canvas;
  --dp-hover-text-color: @text-darkest;
  --dp-primary-color: @bg-brand-light;
  --dp-primary-disabled-color: @bg-dark;
  --dp-primary-text-color: @text-dark;
  --dp-secondary-color: @text-lighter;
}

:root {
  --dp-font-size: 12px;
  --dp-cell-size: 32px;
}
</style>
